import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  CompareArrows,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from '@mui/icons-material';
import contractService from '../../services/contractService';
import moment from 'moment';

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedContract, setSelectedContract] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const data = await contractService.getContractDifferences();
      setContracts(data);
    } catch (error) {
      console.error('Error loading contracts:', error);
      setError('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedContract(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    return moment(date).format('MMM DD, YYYY hh:mm A');
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'Active', color: 'primary', icon: <CheckCircle fontSize="small" /> },
      completed: { label: 'Completed', color: 'success', icon: <CheckCircle fontSize="small" /> },
      disputed: { label: 'Disputed', color: 'error', icon: <ErrorIcon fontSize="small" /> },
      pending: { label: 'Pending', color: 'warning', icon: <Warning fontSize="small" /> },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const getPaymentStatusChip = (status) => {
    const statusConfig = {
      paid: { label: 'Paid', color: 'success' },
      partialpaid: { label: 'Partial', color: 'warning' },
      pending: { label: 'Pending', color: 'error' },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const hasDiscrepancy = (customerContract, partnerContract) => {
    if (!customerContract || !partnerContract) return false;

    return (
      customerContract.amount !== partnerContract.amount ||
      customerContract.estimatedDays !== partnerContract.estimatedDays ||
      customerContract.workDetails !== partnerContract.workDetails
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Contract Management
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Compare and manage customer vs partner contracts
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Request ID</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Customer Amount</TableCell>
              <TableCell>Partner Amount</TableCell>
              <TableCell>Discrepancy</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contracts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary">No contracts found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              contracts.map((item) => {
                const { requestId, customerContract, partnerContract } = item;
                const hasIssue = hasDiscrepancy(customerContract, partnerContract);

                return (
                  <TableRow key={requestId} hover>
                    <TableCell>
                      <Chip label={requestId} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{customerContract?.categoryName || partnerContract?.categoryName}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(customerContract?.amount || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(partnerContract?.amount || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {hasIssue ? (
                        <Chip
                          label="Yes"
                          color="error"
                          size="small"
                          icon={<CompareArrows fontSize="small" />}
                        />
                      ) : (
                        <Chip
                          label="No"
                          color="success"
                          size="small"
                          icon={<CheckCircle fontSize="small" />}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(customerContract?.status || partnerContract?.status)}
                    </TableCell>
                    <TableCell>
                      {getPaymentStatusChip(customerContract?.paymentStatus || 'pending')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleViewContract(item)}
                        title="View Details"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Contract Comparison Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompareArrows />
            <Typography variant="h6">Contract Comparison</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedContract && (
            <Grid container spacing={2}>
              {/* Customer Contract */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Customer Contract
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Category</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedContract.customerContract?.categoryName || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {formatCurrency(selectedContract.customerContract?.amount || 0)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Estimated Days</Typography>
                      <Typography variant="body1">
                        {selectedContract.customerContract?.estimatedDays || 'N/A'} days
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Amount Range</Typography>
                      <Typography variant="body1">
                        {selectedContract.customerContract?.range || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Work Details</Typography>
                      <Typography variant="body2">
                        {selectedContract.customerContract?.workDetails || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      {getStatusChip(selectedContract.customerContract?.status)}
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">Created</Typography>
                      <Typography variant="caption">
                        {formatDate(selectedContract.customerContract?.createdAt)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Partner Contract */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderLeft: '4px solid #4caf50' }}>
                  <CardContent>
                    <Typography variant="h6" color="success.main" gutterBottom>
                      Partner Contract
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Category</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedContract.partnerContract?.categoryName || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        {formatCurrency(selectedContract.partnerContract?.amount || 0)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Estimated Days</Typography>
                      <Typography variant="body1">
                        {selectedContract.partnerContract?.estimatedDays || 'N/A'} days
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Amount Range</Typography>
                      <Typography variant="body1">
                        {selectedContract.partnerContract?.range || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Work Details</Typography>
                      <Typography variant="body2">
                        {selectedContract.partnerContract?.workDetails || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      {getStatusChip(selectedContract.partnerContract?.status)}
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">Created</Typography>
                      <Typography variant="caption">
                        {formatDate(selectedContract.partnerContract?.createdAt)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Discrepancy Alert */}
              {hasDiscrepancy(selectedContract.customerContract, selectedContract.partnerContract) && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<CompareArrows />}>
                    <Typography variant="body2" fontWeight="bold">
                      Discrepancy Detected
                    </Typography>
                    <Typography variant="body2">
                      There are differences between customer and partner contracts.
                      Please review and resolve the discrepancies.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contracts;
